<template>
  <div>
    <PortalNavBar
      class="hidden lg:flex"
    ></PortalNavBar>
    <hr class="border-t border-dark">
    <section class="mx-4 lg:mr-0 lg:ml-12 my-10" v-if="featured.length > 0">
      <ClientOnly>
        <FeaturedMeetingTypesSlider :meetingTypes="featured" />
      </ClientOnly>
    </section>
    <div className="filtering sticky top-0 z-10 bg-primary-contrast">
      <!-- Mobile -->
      <div class="lg:hidden mb-5">
        <PortalMobileFilterHeader
        @searchEvents="searchEvents"
          @updateOrdering="updateOrdering"
          :ordering="ordering"
          :orderingOptions="orderingOptions"
          :categories="categories"
          :selectedCategoriesIds="selectedCategoriesIds"
          :showDatePicker="false"
          @toggleCategory="toggleCategory"
          v-model:search="search"
          @clearAll="clearAll"
          :isAnyFilterActive="isAnyFilterActive"
          :numberOfAppliedFilters="numberOfAppliedFilters"
          :showIncludePastEvents="false"
        />
      </div>

      <!-- Desktop -->
      <PortalFilterHeader
        class="hidden lg:flex"
        :categories="categories"
        :selectedCategoriesIds="selectedCategoriesIds"
        :showDatePicker="false"
        @toggleCategory="toggleCategory"
        @searchEvents="searchEvents"
        v-model:search="search"
      />

      <div class="py-7 px-12 gap-4 hidden lg:flex">
        <div
          class="overflow-x-hidden pr-5 flex items-center"
          v-show="selectedCategoriesIds.length > 0"
        >
          <div class="relative max-w-full">
            <Swiper
              :slides-per-view="'auto'"
              :spaceBetween="10"
              :modules="[SwiperNavigation]"
              :navigation="{
                nextEl: '.selected-next',
                prevEl: '.selected-prev',
              }"
            >
              <SwiperSlide
                class="w-auto slide"
                v-for="(category, $index) in selectedCategories"
                :key="$index"
              >
                <WellTag
                  @click="toggleCategory(category)"
                  :showCloseIcon="true"
                >
                  {{ category.name }}
                </WellTag>
              </SwiperSlide>
            </Swiper>
            <div class="custom-swiper-nav custom-swiper-prev bg-primary-contrast selected-prev h-6 flex items-center pr-2">
              <WellIcon icon="arrow-left" :size="24" />
            </div>
            <div class="custom-swiper-nav custom-swiper-next bg-primary-contrast selected-next h-6 flex items-center">
              <WellIcon icon="arrow-right" :size="24" />
            </div>
          </div>
        </div>
        <div
          class="flex items-center flex-1 justify-between gap-20"
        >
          <div>
            <span
              class="whitespace-nowrap cursor-pointer underline"
              @click="clearAll"
              v-if="isAnyFilterActive"
            >
              Clear All
            </span>
          </div>
          <div class="flex items-center gap-2">
            <SortBySelect
              :ordering="ordering"
              :orderingOptions="orderingOptions"
              @update:ordering="updateOrdering"
            />
          </div>
        </div>
      </div>
    </div>


    <ClientOnly>
      <NoEventFound v-if="meetingTypes.length === 0 && isAnyFilterActive" />
      <div
        class="
          grid grid-cols-1 mx-4 lg:mx-11 mb-10 gap-11
          cards-layout-2:grid-cols-2
          cards-layout-3:grid-cols-3
          cards-layout-4:grid-cols-4
          cards-layout-5:grid-cols-5
          cards-layout-6:grid-cols-6
        "
        v-else
      >
        <NuxtLink
          class="flex flex-row justify-between gap-6 flex-wrap width-full"
          :to="`/sessions/${meetingType.wwwSlug}`"
          v-for="meetingType in meetingTypes" 
          :key="meetingType.wwwSlug" 
        >
          <MeetingTypeCard 
            size="full"
            :meetingType="meetingType" 
          />
        </NuxtLink>
      </div>
      <InfiniteLoading distance="150" @infinite="loadMoreMeetingTypes" :firstload="true" v-if="hasMoreMeetingTypes" :key="infinityLoaderRefresh">
        <template #spinner>
          <div class="flex justify-center items-center py-10">
            Loading...
          </div>
        </template>
        <template #complete>
          <div></div>
        </template>
      </InfiniteLoading>
    </ClientOnly>
  </div>
</template>
  
<script setup lang="ts">
import PortalNavBar from '@/components/layout/PortalNavBar.vue'
import MeetingTypeCard from '@/components/sessions/MeetingTypeCard.vue'
import PortalFilterHeader from '@/components/layout/PortalFilterHeader.vue'
import PortalMobileFilterHeader from '@/components/layout/PortalMobileFilterHeader.vue'
import WellTag from '@/components/common/WellTag.vue';
import WellIcon from '@/components/common/WellIcon.vue';
import { useEventsUrlFiltering } from '@/composables/useEventsUrlFiltering';
import NoEventFound from '@/components/events/NoEventFound.vue';
import SortBySelect from '@/components/common/SortBySelect.vue';
import InfiniteLoading from 'v3-infinite-loading';
import FeaturedMeetingTypesSlider from '@/components/sessions/FeaturedMeetingTypesSlider.vue';
definePageMeta({
  hideHeader: true
})

const { fetchMeetingsTypes, fetchMeetingTypeCategories, fetchFeaturedMeetingTypes } = useApi()
const route = useRoute()

const {
  getFilteringFromQuery,
  clearAll,
  ordering,
  orderingOptions,
  updateOrdering,
  search,
  searchEvents,
  selectedCategoriesIds,
  toggleCategory,
  isAnyFilterActive,
  numberOfAppliedFilters,
} = useEventsUrlFiltering()
const page: Ref<number> = ref(1)
const meetingTypes: Ref<MeetingTypeResponse[]> = ref([])
const fetchMeetingTypesPromise = ref<Promise<MeetingTypeResponse[]> | null>(null)
const fetchMeetingTypesIsLoading = ref(false)
const infinityLoaderRefresh = ref(0)
const hasMoreMeetingTypes = ref(false)

const [
  { data: meetingTypesRes, error: meetingTypesError },
  { data: categories, error: categoriesError },
  { data: featured, error: featuredError }
] = await Promise.all([
  useAsyncData('meetingTypes', () => fetchMeetingsTypes(getFilteringFromQuery(page.value))),
  fetchMeetingTypeCategories(),
  useAsyncData('featured', () => fetchFeaturedMeetingTypes())
])
meetingTypes.value = meetingTypesRes.value.results    
hasMoreMeetingTypes.value = !!meetingTypesRes.value.next

watch(() => route.query, async () => {
  page.value = 1
  const filtering = getFilteringFromQuery(page.value)
  fetchMeetingTypesPromise.value = fetchMeetingsTypes(filtering)
  fetchMeetingTypesIsLoading.value = true
  const { results, next } = await fetchMeetingTypesPromise.value
  hasMoreMeetingTypes.value = !!next
  infinityLoaderRefresh.value++
  meetingTypes.value = results
  fetchMeetingTypesIsLoading.value = false
})

if (meetingTypesError.value) {
  console.error('Error fetching meeting types:', meetingTypesError.value)
}

if (categoriesError.value) {
  console.error('Error fetching categories:', categoriesError.value)
}

if (featuredError.value) {
  console.error('Error fetching featured meeting types:', featuredError.value)
}

const selectedCategories = computed(() => {
  return categories.value.filter((category) => selectedCategoriesIds.value.includes(category.id));
});

const loadMoreMeetingTypes = async ($state) => {
  const nextPage = page.value + 1

  const { next, results } = await fetchMeetingsTypes(getFilteringFromQuery(nextPage))
  if (!next) {
    $state.complete()
  } else {
    $state.loaded()
  }
  page.value = nextPage;
  meetingTypes.value = [...meetingTypes.value, ...results]
}
</script>

<style lang="scss" scoped>
// swiper
.slide {
  width: auto;
}

.custom-swiper-nav {
  position: absolute;
  top: 0;
  z-index: 1;
}

.custom-swiper-prev {
  left: 0;
}

.custom-swiper-next {
  right: -20px;
}

.custom-swiper-nav.swiper-button-disabled {
  display: none;
}
</style>